import useTiclickTableConfig from '@shared/hooks/use-ticlick-table-config';
import { Button } from '@src/design-system/button';
import AddIcon from '@shared/assets/icons/shared/add-icon';
import { TABLES_KEYS } from '@shared/constants/tables-keys';
import type { ActionItemProps } from '@src/design-system/molecules/ticlick-table/ticlick-table-table-actions';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import { useHasPermission } from '@shared/hooks/use-has-permission';
import { entities } from '@shared/constants/entities';
import { CustomFieldListDialogTrigger } from '@src/shared/features/custom-fields/components/custom-field-list-dialog-trigger';
import ExportEntity from '@src/shared/components/export-entity';
import universitiesApi from '../services/universities-api';
import { universitiesColumns } from '../tables/universities-columns';
import type { TUniversity } from '../types';
import RowActionsCell from '../tables/row-actions-cell';
import CreateUniversityModal from '../components/create-university-modal';
import { BulkDeleteAction } from '@src/design-system/molecules/ticlick-table/actions';
import BulkAssignLabels from '@src/shared/tables/table-actions/bulk-assign-labels';
import { CUSTOM_FIELD_ENTITIES } from '@src/shared/types/custom-field';

export function useUniversitiesTable() {
  const { mutateAsync: bulkDeleteUniversity, isPending: bulkDeletePending } =
    universitiesApi.useBulkDelete();
  const hasPermission = useHasPermission();
  const canAddUniversity = hasPermission(entities.university, 'add');
  const canEditUniversity = hasPermission(entities.university, 'change');
  const canDeleteUniversity = hasPermission(entities.university, 'delete');
  const { mutateAsync: bulkAssignLabels, isPending: bulkAssignLabelsPending } =
    universitiesApi.useBulkAssignUniversitiesLabels();
  const actionsTable: ActionItemProps<TUniversity>[] = [
    {
      key: 'bulkAssignLabels',
      render: (row) => {
        const ids = row.getSelectedRowModel().rows.map((r) => r.original.id);
        const handleBulkAssignLabels = (labelsIds: number[]) => {
          bulkAssignLabels(
            { ids, labels: labelsIds },
            {
              onSuccess: () => {
                row.resetRowSelection();
              },
            }
          );
        };
        return (
          <BulkAssignLabels
            bulkAssignLabelFn={handleBulkAssignLabels}
            isAssigningLabelsPending={bulkAssignLabelsPending}
          />
        );
      },
      minWidth: 100,
      visibility: 'selection',
      isDisplay: canEditUniversity,
    },
    {
      key: 'delete',
      render: (row) => {
        const ids = row.getSelectedRowModel().rows.map((r) => r.original.id);
        return (
          <BulkDeleteAction
            isLoading={bulkDeletePending}
            onConfirm={(closeModal) => {
              bulkDeleteUniversity(
                { ids },
                {
                  onSuccess: () => {
                    row.resetRowSelection();
                    closeModal();
                  },
                }
              );
            }}
            entityLabel='universities'
          />
        );
      },
      minWidth: 100,
      visibility: 'selection',
      isDisplay: canDeleteUniversity,
    },
    {
      key: 'export',
      render: (table) => {
        const viewId = table.options.meta?.state?.currentView?.id;
        return (
          <ExportEntity
            entity='UNIVERSITIES'
            view={viewId}
          />
        );
      },
      minWidth: 100,
      visibility: 'none-selection',
    },
    {
      key: 'add',
      visibility: 'none-selection',
      minWidth: 77,
      render: () => (
        <Dialog>
          <DialogTrigger>
            <Button
              variant='primary'
              className='h-9 w-[77px] pr-5 text-sm font-medium rounded-xl'
            >
              <AddIcon />
              Add
            </Button>
          </DialogTrigger>
          <DialogContent
            className='max-w-[600px] rounded-2xl!'
            defaultValue={'add_university'}
          >
            <CreateUniversityModal />
          </DialogContent>
        </Dialog>
      ),
      isDisplay: canAddUniversity,
    },
    {
      key: 'custom-fields',
      visibility: 'none-selection',
      minWidth: 77,
      render: () => <CustomFieldListDialogTrigger />,
    },
  ];

  const table = useTiclickTableConfig({
    options: { columns: universitiesColumns },
    identifyingData: {
      tableKey: TABLES_KEYS.universitiesTable,
      customFieldEntityType: CUSTOM_FIELD_ENTITIES.university,
    },
    fetchingData: {
      fetchFn: universitiesApi.useFetch,
    },
    rowActions: [
      {
        key: 'actions',
        render: (row) => <RowActionsCell row={row} />,
        minWidth: 20,
      },
    ],
  });

  return {
    table,
    actionsTable,
  };
}
